import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import {
  BookOpenText,
  ChevronDown,
  Ellipsis,
  Plus,
  Search,
} from "lucide-react";

import { Box, Flex } from "@/components/layout/flexbox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { BLUE_TEXT_COLOR } from "@/css/constants";
import { WorkspaceContextFragment } from "@/graphql";
import { cn } from "@/lib/utils";

export interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  onCreate?: () => void;
  selectedContexts?: WorkspaceContextFragment[];
  onDelete?: (contextId: string) => void;
  onEdit?: (contextId: string) => void;
  totalRecords?: number;
  /**
   * Optional async search function. Receives the search query and returns a Promise of Option[].
   */
  onSearch?: (query: string) => Promise<void>;
}

export function ScenarioContextMultiSelect({
  options,
  selected,
  onChange,
  selectedContexts,
  onCreate,
  onSearch,
  onDelete,
  totalRecords,
  onEdit,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<Option[]>(options);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Handle immediate search query state update
  const handleSearchInputChange = (query: string) => {
    setSearchQuery(query);
  };

  // Debounce mechanism
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle the actual search when debounced value changes
  useEffect(() => {
    const performSearch = async () => {
      if (isSearching) return;

      setIsSearching(true);
      try {
        if (onSearch) {
          await onSearch(debouncedSearchQuery);
        } else {
          // Local filter by label
          if (debouncedSearchQuery.trim() === "") {
            setSearchResults(options);
          } else {
            setSearchResults(
              options.filter((option) =>
                option.label
                  .toLowerCase()
                  .includes(debouncedSearchQuery.toLowerCase()),
              ),
            );
          }
        }
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearchQuery, onSearch, options]);

  const handleSelect = React.useCallback(
    (value: string) => {
      const updatedSelected = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      onChange(updatedSelected);
    },
    [selected, onChange],
  );

  // This function is kept for backward compatibility but delegates to the input change handler
  const handleSearch = useCallback(
    (query: string) => {
      handleSearchInputChange(query);
    },
    [handleSearchInputChange],
  );

  useEffect(() => {
    setSearchResults(options);
  }, [options]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button intent="secondary" variant="outline" size="sm">
          <BookOpenText />
          {selectedContexts && selectedContexts.length > 0
            ? `Context (${selectedContexts.length})`
            : "Add Context"}
          <ChevronDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Flex className="flex-column h-full w-full flex-col items-center gap-2 overflow-hidden rounded-md bg-white p-2">
          <Box className="text-sm">
            Add Context {totalRecords ? `(${totalRecords})` : ""}
          </Box>
          <Flex className="relative w-full items-center gap-2 rounded p-2">
            <Input
              placeholder="Search options..."
              className="h-9 w-full"
              style={{
                paddingLeft: "2rem",
              }}
              value={searchQuery}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <Search className="absolute left-4 h-5 w-5 text-gray-600" />
          </Flex>
          {searchResults.map((option) => {
            const isSelected = selected.includes(option.value);

            return (
              <Box
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className="flex w-full items-center justify-between rounded p-2 hover:cursor-pointer hover:bg-gray-200"
              >
                <Flex className="items-center justify-between gap-2">
                  <Checkbox className={cn("h-5 w-5")} checked={isSelected} />
                  <span
                    className={cn({ "font-semibold": isSelected }, "text-sm")}
                  >
                    {option.label}
                  </span>
                </Flex>
                <div className="flex items-center gap-2">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        className="h-6 w-6 bg-white text-gray-600"
                        variant="glass"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Open menu"
                      >
                        <Ellipsis className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEdit) {
                            onEdit(option.value);
                          }
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDelete) onDelete(option.value);
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Box>
            );
          })}

          {onCreate && (
            <Flex
              onClick={onCreate}
              className="w-full items-center gap-2 rounded p-2 pt-2 hover:cursor-pointer hover:bg-gray-200"
            >
              <Plus color={BLUE_TEXT_COLOR} />
              <Box className="text-sm" color={BLUE_TEXT_COLOR}>
                Create Saved Context
              </Box>
            </Flex>
          )}
        </Flex>
      </PopoverContent>
    </Popover>
  );
}
